/**
 * OLFACTUM — Delight Layer
 * Scroll-reveal animations & hero entrance sequence.
 * Pure vanilla JS, no dependencies.
 * Respects prefers-reduced-motion.
 */
(function () {
  'use strict';

  /* ── Bail if user prefers reduced motion ──── */
  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReduced) {
    /* Make everything visible immediately */
    document.querySelectorAll(
      '.hero, .guarantee-section, .fp-grid, .fp-section, .section-manifesto, ' +
      '.section-about-mission, .quiz-cta, .social-proof, .combos-section, ' +
      '.mystery-section, .prod-carousel, .ugc-feed, ' +
      '.cp-header, .cp-grid, .cp-section-divider, .cp-cta, .cp, ' +
      '.col-main, .col-sidebar, .products-grid, .col-pagination, .col-empty'
    ).forEach(function (el) { el.classList.add('revealed'); });

    var hero = document.querySelector('.hero');
    if (hero) hero.classList.add('hero--entered');
    return;
  }

  /* ── Hero Entrance Sequence ───────────────── */
  function initHeroEntrance() {
    var hero = document.querySelector('.hero');
    if (!hero) return;

    /* Trigger entrance after a brief paint delay */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        hero.classList.add('hero--entered');
      });
    });
  }

  /* ── Scroll Reveal Observer ───────────────── */
  function initScrollReveals() {
    var revealTargets = [
      '.guarantee-section',
      '.fp-grid',
      '.fp-section',
      '.section-manifesto',
      '.section-about-mission',
      '.quiz-cta',
      '.social-proof',
      '.combos-section',
      '.mystery-section',
      '.prod-carousel',
      '.ugc-feed',
      '[data-reveal]',
      '[data-reveal-stagger]'
    ];

    var elements = document.querySelectorAll(revealTargets.join(','));
    if (elements.length === 0) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.12,
      rootMargin: '0px 0px -40px 0px'
    });

    elements.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ── Eyebrow Line-Draw Setup ──────────────── */
  function initEyebrowLines() {
    var eyebrow = document.querySelector('.mystery-section__eyebrow');
    if (!eyebrow) return;

    /* Start with lines hidden */
    eyebrow.setAttribute('data-line-hidden', '');

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.removeAttribute('data-line-hidden');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });

    observer.observe(eyebrow);
  }

  /* ── Mystery Section Header Reveal ────────── */
  function initMysteryReveal() {
    var header = document.querySelector('.mystery-section__header');
    if (!header) return;

    var children = header.children;
    for (var i = 0; i < children.length; i++) {
      children[i].style.opacity = '0';
      children[i].style.transform = 'translateY(12px)';
      children[i].style.transition =
        'opacity 0.6s cubic-bezier(0.16,1,0.3,1) ' + (i * 100) + 'ms, ' +
        'transform 0.6s cubic-bezier(0.16,1,0.3,1) ' + (i * 100) + 'ms';
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          for (var i = 0; i < children.length; i++) {
            children[i].style.opacity = '1';
            children[i].style.transform = 'translateY(0)';
          }
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });

    observer.observe(header);
  }

  /* ── Combos Page: Header + Grid + Divider + CTA reveals ── */
  function initCombosPageReveals() {
    var combosTargets = [
      '.cp-header',
      '.cp-grid',
      '.cp-section-divider',
      '.cp-cta',
      '.cp'
    ];

    var elements = document.querySelectorAll(combosTargets.join(','));
    if (elements.length === 0) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -30px 0px'
    });

    elements.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ── Combos Page: Tab Underline Indicator ──── */
  function initCombosTabIndicator() {
    var tabs = document.querySelector('.cp-tabs');
    if (!tabs) return;

    function updateIndicator() {
      var active = tabs.querySelector('.cp-tab.is-active');
      if (!active) return;

      var tabsRect = tabs.getBoundingClientRect();
      var activeRect = active.getBoundingClientRect();

      tabs.style.setProperty('--tab-indicator-left', (activeRect.left - tabsRect.left + tabs.scrollLeft) + 'px');
      tabs.style.setProperty('--tab-indicator-width', activeRect.width + 'px');
      tabs.classList.add('has-indicator');
    }

    /* F3A-thrash: replaced MutationObserver(attributeFilter:['class']) with event delegation.
       Previously we observed every .cp-tab for class mutations caused by Alpine's :class
       binding on activeTab changes. Instead, listen for clicks on the tabs container — Alpine
       updates activeTab synchronously on click, so we just wait one animation frame for the
       DOM to reflect the new `is-active` class, then reposition the indicator. */
    function onTabsClick(ev) {
      if (!ev.target.closest('.cp-tab')) return;
      /* Double rAF ensures Alpine's class binding has been flushed before we measure. */
      requestAnimationFrame(function () {
        requestAnimationFrame(updateIndicator);
      });
    }
    tabs.addEventListener('click', onTabsClick);

    /* Keyboard activation (Enter/Space) also triggers the click event via button default,
       so the single click listener covers both mouse and keyboard users. */

    /* Cleanup on navigation to prevent memory leak */
    window.addEventListener('pagehide', function () {
      tabs.removeEventListener('click', onTabsClick);
    }, { once: true });

    /* Reposition on window resize (the indicator width/left depend on layout). */
    var resizeRaf = 0;
    function onResize() {
      if (resizeRaf) return;
      resizeRaf = requestAnimationFrame(function () {
        updateIndicator();
        resizeRaf = 0;
      });
    }
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('pagehide', function () {
      window.removeEventListener('resize', onResize);
    }, { once: true });

    /* Initial position after a frame (Alpine needs to set is-active) */
    requestAnimationFrame(function () {
      requestAnimationFrame(updateIndicator);
    });
  }

  /* ── Catalog Page: Section + Sidebar + Grid + Pagination reveals ── */
  function initCatalogPageReveals() {
    var catalogTargets = [
      '.col-main',
      '.col-sidebar',
      '.products-grid',
      '.col-pagination',
      '.col-empty'
    ];

    var elements = document.querySelectorAll(catalogTargets.join(','));
    if (elements.length === 0) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -30px 0px'
    });

    elements.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ── Init ──────────────────────────────────── */
  function init() {
    initHeroEntrance();
    initScrollReveals();
    initEyebrowLines();
    initMysteryReveal();
    initCombosPageReveals();
    initCombosTabIndicator();
    initCatalogPageReveals();
  }

  /* Run on DOMContentLoaded or immediately if already loaded */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
